import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ExternalLink, ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { getWbHubById, getHubProxyUrl } from '../../config/hubRegistry';
import { useFeatureStore, resolveHubModKey } from '../../stores/featureStore';

const HubLauncher: React.FC = () => {
  const { hubName } = useParams<{ hubName: string }>();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hub = hubName ? getWbHubById(hubName) : undefined;
  const hubUrl = hub ? getHubProxyUrl(hub) : '';

  // Module gate: check per-hub override first, then category key
  const { isModuleEnabled, loaded } = useFeatureStore();
  const modKey = hub ? resolveHubModKey(hub.id, hub.category) : undefined;

  const handleLoad = useCallback(() => {
    setLoading(false);
    setError(false);
  }, []);

  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  const handleRetry = useCallback(() => {
    setLoading(true);
    setError(false);
    if (iframeRef.current) {
      iframeRef.current.src = hubUrl;
    }
  }, [hubUrl]);

  // Gate after all hooks (React rules-of-hooks)
  if (modKey && !loaded) {
    return <div role="status" style={{ padding: 32 }}>Loading module…</div>;
  }
  if (modKey && !isModuleEnabled(modKey)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!hubName || !hub) {
    return (
      <div style={{ padding: '48px 32px', textAlign: 'center' }}>
        <AlertTriangle size={40} color="#d97706" style={{ marginBottom: 12 }} />
        <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
          Hub not found
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 20px 0' }}>
          No hub registered for "{hubName}"
        </p>
        <button
          onClick={() => navigate('/hubs')}
          style={{
            background: 'rgba(13,148,136,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            color: '#0D9488',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ArrowLeft size={14} /> Back to Toolbox
        </button>
      </div>
    );
  }

  if (hub.comingSoon) {
    return (
      <div style={{ padding: '48px 32px', textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'rgba(245,158,11,0.1)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={28} color="#f59e0b" />
        </div>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
          {hub.name}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 20px 0' }}>
          This hub is coming soon. Check back later.
        </p>
        <button
          onClick={() => navigate('/hubs')}
          style={{
            background: 'rgba(13,148,136,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            color: '#0D9488',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ArrowLeft size={14} /> Back to Toolbox
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          borderBottom: '1px solid var(--card-border)',
          background: 'var(--card-bg)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => navigate('/hubs')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: '4px 6px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
          }}
          title="Back to Toolbox"
        >
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{hub.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{hub.description}</span>
        </div>
        <button
          onClick={handleRetry}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: '4px 6px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
          }}
          title="Reload hub"
        >
          <RefreshCw size={14} />
        </button>
        <button
          onClick={() => window.open(hubUrl, '_blank')}
          style={{
            background: 'rgba(13,148,136,0.1)',
            border: 'none',
            borderRadius: 6,
            padding: '4px 10px',
            color: '#0D9488',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
          title="Open in new tab"
        >
          <ExternalLink size={12} /> Pop out
        </button>
      </div>

      {/* Loading overlay */}
      {loading && !error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            color: 'var(--text-muted)',
            fontSize: 14,
            gap: 10,
          }}
        >
          <RefreshCw size={16} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
          Loading {hub.name}...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 48, gap: 12 }}>
          <AlertTriangle size={32} color="#dc2626" />
          <p style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 500, margin: 0 }}>
            Unable to load {hub.name}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            The hub may be starting up or temporarily unavailable.
          </p>
          <button
            onClick={handleRetry}
            style={{
              background: 'rgba(13,148,136,0.1)',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              color: '#0D9488',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={hubUrl}
        onLoad={handleLoad}
        onError={handleError}
        title={hub.name}
        style={{
          flex: 1,
          width: '100%',
          border: 'none',
          display: error ? 'none' : 'block',
          minHeight: 0,
        }}
      />

      {/* Inline keyframe for spin animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default HubLauncher;
